import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { fetchLocalization } from "@/utils/localization";
import { useDispatch } from "react-redux";
import { setSearchValues } from "@/redux/reducer/initSearchSlice";
import { useRouter } from "next/router";
import { useUserJourney } from "@/hooks/useUserJourney";

type Lang = "en" | "ar";
type Dir = "ltr" | "rtl";

export type LocalizationState = {
  lang: Lang;
  currency: string;
  nationality: string;
  dir: Dir;
};

type LocalizationContextType = {
  localization: LocalizationState | null;
  setLocalization: (data: Partial<LocalizationState>) => void;
  loading: boolean;
};

const LocalizationContext = createContext<LocalizationContextType>({
  localization: null,
  setLocalization: () => {},
  loading: true,
});

export const LocalizationProvider = ({ children }: { children: ReactNode }) => {
  const [localization, setLocalizationState] =
    useState<LocalizationState | null>(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const router = useRouter();
  const { trackEvent, getFullUrl } = useUserJourney("1231231211");

  useEffect(() => {
    if (localization?.currency && localization?.nationality) {
      dispatch(
        setSearchValues({
          currency: localization.currency,
          nationality: localization.nationality,
        })
      );
    }
  }, [localization?.currency, localization?.nationality, dispatch]);

  useEffect(() => {
    const loadLocalization = async () => {
      try {
        const stored = localStorage.getItem("localization");
        if (stored) {
          const parsed: LocalizationState = JSON.parse(stored);
          setLocalizationState(parsed);
          document.documentElement.setAttribute("dir", parsed.dir);
          setLoading(false);
        } else {

          // const result = await fetchLocalization({ device: "desktop" });
          // if (result?.code === 200) {
          //   const lang = result.response.lang as Lang;
          //   const dir: Dir = lang === "ar" ? "rtl" : "ltr";
          //   const initial: LocalizationState = {
          //     lang,
          //     currency: result.response.currency,
          //     nationality: result.response.nationality,
          //     dir,
          //   };
             const initial: LocalizationState = {
             lang: "en",
              currency: "AED",
              nationality: "AE",
              dir:"ltr",
            };
            const dir="ltr";
            setLocalizationState(initial);
            localStorage.setItem("localization", JSON.stringify(initial));
            document.documentElement.setAttribute("dir", dir);
          //   trackEvent({
          //     page: router.pathname.replace("/", ""),
          //     url: getFullUrl(),
          //     action: "localization",
          //     api_payload: { device: "desktop" },
          //     api_response: result,
          //     api_error: false,
          //   });
          // } else {
          //   console.error("Failed to load localization from API");
          // }
          setLoading(false);
        }
      } catch (err) {
        console.error("Localization fetch/load failed:", err);
        setLoading(false);
      }
    };

    loadLocalization();
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem("localization");
      if (stored) {
        const parsed: LocalizationState = JSON.parse(stored);
        setLocalizationState(parsed);
        document.documentElement.setAttribute("dir", parsed.dir);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!localization?.lang || router.locale === localization.lang) return;

    const langPattern = /^\/(ar|en)(?=\/|$)/;
    const pathWithoutLocale = router.asPath.replace(langPattern, "");
    const newPath = `/${localization.lang}${pathWithoutLocale}`;

    if (router.asPath !== newPath) {
      router.replace(newPath, undefined, { locale: localization.lang });
    }
  }, [localization?.lang]);

  const setLocalization = (data: Partial<LocalizationState>) => {
    setLocalizationState((prev) => {
      const lang = data.lang || prev?.lang || "en";
      const dir: Dir = lang === "ar" ? "rtl" : "ltr";
      const updated = {
        ...prev,
        ...data,
        dir,
      } as LocalizationState;
      localStorage.setItem("localization", JSON.stringify(updated));
      document.documentElement.setAttribute("dir", dir);
      window.dispatchEvent(new Event("storage"));
      return updated;
    });
  };

  return (
    <LocalizationContext.Provider
      value={{ localization, setLocalization, loading }}
    >
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => useContext(LocalizationContext);
